import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { ExportNotification, ExportNotificationService } from '@core/services/export-notification.service';

@Component({
  selector: 'xemex-notification-container',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule, TranslateModule],
  template: `
    <div class="notification-container" [attr.aria-label]="'Notifications'">
      @for (notification of visibleNotifications(); let i = $index; track notification.id) {
        <div
          class="notification"
          [ngClass]="'notification--' + notification.type"
          role="alert"
          [attr.aria-live]="'polite'"
        >
          <div class="notification__content">
            <div class="notification__icon">
              @switch (notification.type) {
                @case ('success') {
                  <mat-icon>check_circle</mat-icon>
                }
                @case ('error') {
                  <mat-icon>cancel</mat-icon>
                }
                @case ('warning') {
                  <mat-icon>warning</mat-icon>
                }
                @default {
                  <mat-icon>info</mat-icon>
                }
              }
            </div>

            <div class="notification__message">
              <div class="notification__title">{{ notification.title }}</div>
              <div class="notification__text">{{ notification.message }}</div>
              @if (notification.errorDetails && notification.type === 'error') {
                <details class="notification__details">
                  <summary>{{ 'dataExport.errorDetails' | translate }}</summary>
                  <pre class="notification__error-trace">{{ notification.errorDetails }}</pre>
                </details>
              }
            </div>
          </div>

          <div class="notification__actions">
            @if (notification.primaryAction) {
              <button
                mat-button
                class="notification__action-button"
                (click)="onActionClick(notification)"
                [disabled]="actionInProgress()"
              >
                {{ notification.primaryAction.label }}
              </button>
            }
            <button
              mat-icon-button
              class="notification__dismiss-button"
              (click)="onDismiss(notification)"
              [attr.aria-label]="'Dismiss notification'"
              title="Dismiss"
            >
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>
      }

      @if (hiddenCount() > 0) {
        <div class="notification-overflow" role="status">
          {{ hiddenCount() }} {{ 'dataExport.moreNotifications' | translate }}
        </div>
      }
    </div>
  `,
  styleUrl: './notification-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationContainerComponent implements OnDestroy {
  private readonly notificationService = inject(ExportNotificationService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly MAX_VISIBLE = 5;
  protected readonly notifications = signal<ExportNotification[]>([]);
  protected readonly actionInProgress = signal(false);

  protected readonly visibleNotifications = signal<ExportNotification[]>([]);
  protected readonly hiddenCount = signal(0);

  constructor() {
    this.notificationService.notifications$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(notifications => {
      this.notifications.set(notifications);
      this.updateVisibleNotifications();
    });

    effect(() => {
      document.addEventListener('keydown', this.handleKeyboardDismiss);
      return () => {
        document.removeEventListener('keydown', this.handleKeyboardDismiss);
      };
    });
  }

  protected onDismiss(notification: ExportNotification): void {
    this.notificationService.dismiss(notification.id);
  }

  protected onActionClick(notification: ExportNotification): void {
    this.actionInProgress.set(true);
    this.notificationService.executeAction(notification.id);
    const timeoutId = setTimeout(() => this.actionInProgress.set(false), 500);
    // Cleanup timeout on component destroy via destroyRef
    this.destroyRef.onDestroy(() => clearTimeout(timeoutId));
  }

  private updateVisibleNotifications(): void {
    const notifications = this.notifications();
    const visible = notifications.slice(0, this.MAX_VISIBLE);
    const hidden = Math.max(0, notifications.length - this.MAX_VISIBLE);

    this.visibleNotifications.set(visible);
    this.hiddenCount.set(hidden);
  }

  private readonly handleKeyboardDismiss = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      const last = this.notifications().at(-1);
      if (last) {
        this.onDismiss(last);
      }
    }
  };

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleKeyboardDismiss);
  }
}
